import type {
  AttemptBulkResponseCreatePayload,
  AttemptResponseCreatePayload,
} from "~/types/admin/assessment";

function hasAudioAnswer(response: AttemptResponseCreatePayload): boolean {
  return response.audio_answer instanceof Blob;
}

function buildAudioFilename(audio: Blob, questionId: number): string {
  const extensionByType: Record<string, string> = {
    "audio/mpeg": "mp3",
    "audio/mp3": "mp3",
    "audio/mp4": "m4a",
    "audio/m4a": "m4a",
    "audio/x-m4a": "m4a",
    "audio/webm": "webm",
    "audio/ogg": "ogg",
    "audio/wav": "wav",
    "audio/wave": "wav",
  };
  const extension = extensionByType[audio.type] || "webm";
  return `speaking-question-${questionId}.${extension}`;
}

function appendResponseFields(
  formData: FormData,
  response: AttemptResponseCreatePayload,
) {
  formData.append("question", String(response.question));

  if (response.text_answer !== undefined && response.text_answer !== null) {
    formData.append("text_answer", response.text_answer);
  }

  (response.selected_options || []).forEach((optionId) => {
    formData.append("selected_options", String(optionId));
  });

  if (response.audio_answer instanceof Blob) {
    formData.append(
      "audio_answer",
      response.audio_answer,
      buildAudioFilename(response.audio_answer, response.question),
    );
  }
}

function responseWithoutAudio(
  response: AttemptResponseCreatePayload,
): AttemptResponseCreatePayload {
  const { audio_answer: _audioAnswer, ...rest } = response;
  return rest;
}

export function buildAttemptResponseSubmissionPayload(
  response: AttemptResponseCreatePayload,
): AttemptResponseCreatePayload | FormData {
  if (!hasAudioAnswer(response)) {
    return response;
  }

  const formData = new FormData();
  appendResponseFields(formData, response);
  return formData;
}

export function buildAttemptBulkResponseSubmissionPayload(
  payload: AttemptBulkResponseCreatePayload,
): AttemptBulkResponseCreatePayload | FormData {
  if (!payload.responses.some(hasAudioAnswer)) {
    return payload;
  }

  const formData = new FormData();
  formData.append(
    "responses",
    JSON.stringify(payload.responses.map(responseWithoutAudio)),
  );

  payload.responses.forEach((response) => {
    if (response.audio_answer instanceof Blob) {
      formData.append(
        `audio_answer_${response.question}`,
        response.audio_answer,
        buildAudioFilename(response.audio_answer, response.question),
      );
    }
  });

  return formData;
}
